import React, { useMemo, useState } from 'react';
import { Briefcase, ChevronDown, ChevronRight, UserCheck, Users } from 'lucide-react';

/**
 * The team as a tree: one card per manager, team leads inside, recruiters
 * inside their team lead. Reading "who is under whom" off a flat table meant
 * following Reports-to names by hand; here the nesting answers it.
 */

const ADMIN_ROLES = ['admin', 'super_admin'];

const ROLE_ORDER = { manager: 0, team_lead: 1, recruiter: 2 };

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  team_lead: 'Team Lead',
  recruiter: 'Recruiter',
};

const ROLE_COLORS = {
  super_admin: '#f87171',
  admin: '#a78bfa',
  manager: '#38bdf8',
  team_lead: '#34d399',
  recruiter: '#94a3b8',
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function initials(name) {
  return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

/** Team leads, recruiters and jobs anywhere under this person. */
function branchStats(id, childrenOf, byId) {
  const stats = { team_lead: 0, recruiter: 0, jobs: byId[id]?.job_count || 0 };
  const stack = [...(childrenOf[id] || [])];
  while (stack.length) {
    const u = stack.pop();
    if (stats[u.role] !== undefined) stats[u.role] += 1;
    stats.jobs += u.job_count || 0;
    stack.push(...(childrenOf[u.id] || []));
  }
  return stats;
}

function StatChips({ stats, role }) {
  const chips = [];
  if (role === 'manager') chips.push([UserCheck, plural(stats.team_lead, 'team lead', 'team leads')]);
  if (role !== 'recruiter') chips.push([Users, plural(stats.recruiter, 'recruiter', 'recruiters')]);
  chips.push([Briefcase, plural(stats.jobs, 'job', 'jobs')]);
  return (
    <div className="org-chips">
      {chips.map(([Icon, text]) => (
        <span key={text} className="org-chip"><Icon size={12} strokeWidth={2} /> {text}</span>
      ))}
    </div>
  );
}

function PersonRow({ user, meId, match, stats, hasChildren, open, onToggle, renderActions, top }) {
  const color = ROLE_COLORS[user.role] || '#94a3b8';
  return (
    <div className={`org-row${top ? ' org-row-top' : ''}${match ? ' org-row-match' : ''}${user.is_active ? '' : ' org-row-inactive'}`}>
      <button
        type="button"
        className="org-toggle"
        onClick={onToggle}
        disabled={!hasChildren}
        aria-label={open ? 'Collapse team' : 'Expand team'}
        style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      <span className="org-avatar" style={{ background: `${color}26`, color, borderColor: `${color}66` }}>
        {initials(user.name)}
      </span>
      <div className="org-person">
        <div className="org-name">
          {user.name}
          {user.id === meId && <span className="org-you"> (you)</span>}
          <span className="org-role" style={{ color, background: `${color}1f`, borderColor: `${color}55` }}>
            {ROLE_LABELS[user.role] || user.role}
          </span>
          {!user.is_active && <span className="org-inactive-tag">Inactive</span>}
        </div>
        <div className="org-email">{user.email}</div>
      </div>
      {stats && <StatChips stats={stats} role={user.role} />}
      <div className="org-actions">{renderActions(user)}</div>
    </div>
  );
}

function Branch({ user, depth, ctx }) {
  const { childrenOf, byId, visible, matches, collapsed, toggle, searching } = ctx;
  const kids = (childrenOf[user.id] || []).filter(k => visible.has(k.id));
  const open = searching || !collapsed.has(user.id);
  return (
    <div className="org-branch">
      <PersonRow
        user={user}
        meId={ctx.meId}
        match={searching && matches.has(user.id)}
        stats={user.role === 'recruiter' ? (user.job_count ? branchStats(user.id, childrenOf, byId) : null) : branchStats(user.id, childrenOf, byId)}
        hasChildren={kids.length > 0}
        open={open}
        onToggle={() => toggle(user.id)}
        renderActions={ctx.renderActions}
        top={depth === 0}
      />
      {open && kids.length > 0 && (
        <div className="org-children">
          {kids.map(k => <Branch key={k.id} user={k} depth={depth + 1} ctx={ctx} />)}
        </div>
      )}
    </div>
  );
}

export default function OrgChart({ users, meId, search, renderActions }) {
  const [collapsed, setCollapsed] = useState(() => new Set());

  const { byId, childrenOf, admins, managers, unplaced, matches, visible } = useMemo(() => {
    const byIdMap = Object.fromEntries(users.map(u => [u.id, u]));
    const children = {};
    const roots = [];
    for (const u of users) {
      if (ADMIN_ROLES.includes(u.role)) continue;
      // A leader outside what this person may see (a team lead's own manager,
      // say) makes them a root of the visible tree.
      if (u.reports_to_id && byIdMap[u.reports_to_id]) {
        (children[u.reports_to_id] = children[u.reports_to_id] || []).push(u);
      } else {
        roots.push(u);
      }
    }
    const byRoleThenName = (a, b) =>
      (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || a.name.localeCompare(b.name);
    Object.values(children).forEach(list => list.sort(byRoleThenName));
    roots.sort(byRoleThenName);

    const q = search.trim().toLowerCase();
    const hit = new Set(q
      ? users.filter(u => `${u.name} ${u.email}`.toLowerCase().includes(q)).map(u => u.id)
      : users.map(u => u.id));
    // Keep every match's leaders on screen, so a result still shows where it sits.
    const shown = new Set();
    for (const id of hit) {
      let cur = byIdMap[id];
      while (cur && !shown.has(cur.id)) {
        shown.add(cur.id);
        cur = cur.reports_to_id ? byIdMap[cur.reports_to_id] : null;
      }
    }

    return {
      byId: byIdMap,
      childrenOf: children,
      admins: users.filter(u => ADMIN_ROLES.includes(u.role) && shown.has(u.id)),
      managers: roots.filter(u => u.role === 'manager' && shown.has(u.id)),
      // Anyone not under a manager: a team lead or recruiter placed directly
      // under the admin, or — for a team lead's own view — the team lead.
      unplaced: roots.filter(u => u.role !== 'manager' && shown.has(u.id)),
      matches: hit,
      visible: shown,
    };
  }, [users, search]);

  const toggle = (id) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const searching = Boolean(search.trim());
  const ctx = { childrenOf, byId, visible, matches, collapsed, toggle, searching, meId, renderActions };
  const managerIds = managers.map(m => m.id);
  const allCollapsed = managerIds.length > 0 && managerIds.every(id => collapsed.has(id));
  // A manager's or team lead's own view has themselves as the only root.
  const ownTreeOnly = unplaced.length === 1 && managers.length === 0 && unplaced[0].id === meId;

  if (!admins.length && !managers.length && !unplaced.length) {
    return <div className="empty-state"><div className="empty-state-text">No one matches “{search}”.</div></div>;
  }

  return (
    <div className="org-chart">
      {admins.length > 0 && (
        <div className="org-admins">
          <span className="org-section-label">Admins</span>
          {admins.map(a => (
            <PersonRow key={a.id} user={a} meId={meId} match={searching && matches.has(a.id)}
              hasChildren={false} open={false} onToggle={() => {}} renderActions={renderActions} />
          ))}
        </div>
      )}

      {managers.length > 0 && (
        <div className="org-section-head">
          <span className="org-section-label">
            {plural(managers.length, 'manager', 'managers')} · each manager's team is separate
          </span>
          {!searching && (
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(managerIds))}>
              {allCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
          )}
        </div>
      )}

      <div className="org-managers">
        {managers.map(m => (
          <div key={m.id} className="org-card">
            <Branch user={m} depth={0} ctx={ctx} />
            {!(childrenOf[m.id] || []).length && (
              <div className="org-empty">No team yet — add a team lead or recruiter under {m.name}.</div>
            )}
          </div>
        ))}
      </div>

      {unplaced.length > 0 && (
        <div className="org-card org-card-unplaced">
          {!ownTreeOnly && (
            <div className="org-unplaced-head">
              <span className="org-section-label">Not under a manager yet</span>
              <span className="org-hint">Use Move to place them in a manager's team.</span>
            </div>
          )}
          {unplaced.map(u => <Branch key={u.id} user={u} depth={0} ctx={ctx} />)}
        </div>
      )}
    </div>
  );
}
